import React, { useState, useEffect, useRef } from "react";
import { useGame } from "../game/em2c-store";

// Tracks what changed while the tab was hidden
let _hiddenSnapshot = null;
let _hiddenAt = null;

function takeSnapshot() {
  const s = useGame.getState();
  const res = {};
  for (const [k, v] of Object.entries(s.resources)) res[k] = v.stored;
  return {
    sol: s.sol,
    time: s.time,
    resources: res,
    colonists: s.colonists.length,
    buildings: s.buildings.length,
    buildingProgress: s.buildings.map((b) => ({ id:b.id, type:b.type, progress:b.progress })),
  };
}

function computeChanges(before, after) {
  const changes = [];
  const solsPassed = after.sol - before.sol;

  for (const [k, beforeVal] of Object.entries(before.resources)) {
    const afterVal = after.resources[k];
    if (afterVal === undefined) continue;
    const diff = afterVal - beforeVal;
    if (Math.abs(diff) < 1) continue;
    changes.push({ label: k.charAt(0).toUpperCase()+k.slice(1), diff: Math.round(diff), positive: diff>0 });
  }

  const completed = after.buildingProgress.filter((b) => {
    const prev = before.buildingProgress.find((x) => x.id===b.id);
    return prev && prev.progress<1 && b.progress>=1;
  });
  if (completed.length>0)
    changes.push({ label:`${completed.length} building${completed.length>1?"s":""} completed`, diff:completed.length, positive:true, isInfo:true });

  const newB = after.buildings - before.buildings;
  if (newB>0)
    changes.push({ label:`${newB} new building${newB>1?"s":""}`, diff:newB, positive:true, isInfo:true });

  const colonistDiff = after.colonists - before.colonists;
  if (colonistDiff<0)
    changes.push({ label:`${Math.abs(colonistDiff)} colonist${Math.abs(colonistDiff)>1?"s":""} lost`, diff:colonistDiff, positive:false, isInfo:true });
  else if (colonistDiff>0)
    changes.push({ label:`${colonistDiff} new colonist${colonistDiff>1?"s":""}`, diff:colonistDiff, positive:true, isInfo:true });

  return { changes, solsPassed };
}

const fmtTime = (s) => {
  if (s<60) return `${s}s`;
  const m = Math.floor(s/60), sec = s%60;
  return `${m}m ${sec}s`;
};

export default function AwaySummary() {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState(null);
  const fadeTimer = useRef(null);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        _hiddenSnapshot = takeSnapshot();
        _hiddenAt = Date.now();
      } else if (_hiddenSnapshot && _hiddenAt) {
        const after = takeSnapshot();
        const { changes, solsPassed } = computeChanges(_hiddenSnapshot, after);
        const awaySeconds = Math.round((Date.now() - _hiddenAt) / 1000);
        if (changes.length>0 || solsPassed>0) {
          setData({ changes, solsPassed, awaySeconds });
          setVisible(true);
          // Fade out after 5 seconds
          if (fadeTimer.current) clearTimeout(fadeTimer.current);
          fadeTimer.current = setTimeout(() => setVisible(false), 5000);
        }
        _hiddenSnapshot = null;
        _hiddenAt = null;
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, []);

  if (!data || data.changes.length === 0 && data.solsPassed === 0) return null;

  return (
    <div
      className="fixed z-40 pointer-events-none select-none left-1/2 top-16"
      style={{
        transform: "translateX(-50%)",
        transition: "opacity 1s ease",
        opacity: visible ? 1 : 0,
        fontFamily: "Rajdhani, sans-serif",
      }}
    >
      <div
        className="px-4 py-3 border border-white/15 shadow-lg"
        style={{ background: "rgba(10,12,16,0.92)", backdropFilter: "blur(8px)", minWidth: 320 }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
          <div className="w-1.5 h-5" style={{ background: "#00e5ff", boxShadow: "0 0 8px #00e5ff" }} />
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-400">Welcome Back</div>
          <div className="ml-auto text-[10px] font-mono text-slate-400">
            Away {fmtTime(data.awaySeconds)}{data.solsPassed>0?` · ${data.solsPassed} sol${data.solsPassed>1?"s":""}`:""}
          </div>
        </div>

        {/* Changes */}
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {data.changes.map((c, i) => (
            <div key={i} className="flex items-center justify-between gap-3 text-[11px] font-mono">
              <span style={{ color: c.positive ? "#7fffd4" : "#ff3366" }}>
                {c.isInfo ? "▸ " : c.positive ? "▲ " : "▼ "}{c.label}
              </span>
              {!c.isInfo && (
                <span
                  className="font-bold"
                  style={{ color: c.positive ? "#00fa9a" : "#ff3366", minWidth: 40, textAlign: "right" }}
                >
                  {c.positive ? "+" : ""}{c.diff}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
